<script setup>
import { reactive, onMounted, onUnmounted, ref, inject, computed, watch, nextTick } from "vue";

import path from 'path';

import useAlertStore from '@/stores/alert.store.js';
import useFileManagerStore from '@/stores/filemanager.store.js';
import {useAuthStore} from '@/stores/auth.store.js';


const alertStore = useAlertStore();
const thisStore = useFileManagerStore();
import axios from "axios";

const authStore = useAuthStore();
const {user} = storeToRefs(authStore);

import { AgGridVue } from "ag-grid-vue3";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";

import { onClickOutside } from '@vueuse/core';

const showContextMenu = inject('$contextmenu');

import { getEventPath, unfocus } from "@/services/utils.js";
import { sizeFormatter, dateFormatter, typeFormatter } from "@/services/file-meta-formatters.js";
import { getFilesFromDataTransferItems } from '@/services/datatransfer-files-webkit-helper';
import fileTypeCellRenderer from "@/components/fileTypeCellRenderer.vue";
import viewButtonCellRenderer from "@/components/viewButtonCellRenderer.vue";
import fileManagerToolbar from "@/components/fileManagerToolbar.vue";
import {storeToRefs} from "pinia";



const currentPath = thisStore.currentPath;
// Empty until onMounted resolves the user's grants. Both onMounted and
// onGridReady can call listFiles; whichever loses the race bails on
// the empty value rather than firing a doomed request against "/".
const selectedRootPath = ref('');

const fileManagerBoxRef = ref(null);

const gridApi = ref(null);
const gridElementRef = ref(null);
const toolbarRef = ref(null);

const status = ref(false);
const selectedNodes = ref([]);
const selectedDataSizes = ref([]);
const selectedDataTotal = ref(0);
const isFileDraggedOver = ref(false);

const rootPaths = ref([]);

const quickFilter = ref('');

const onQuickFilterInput = () => {
    if (gridApi.value) {
        gridApi.value.setGridOption('quickFilterText', quickFilter.value);
    }
};

const clearQuickFilter = () => {
    quickFilter.value = '';
    onQuickFilterInput();
};


const rowData = reactive({});

const columnDefs = reactive({value: [
    {
        headerName: '',
        field: 'filetype',
        width: 160,
        cellRenderer: fileTypeCellRenderer,
        filterParams: { newRowsAction: "keep" },
        // Embed the row checkbox in this column rather than letting
        // ag-grid v32 auto-inject a separate checkbox column. The
        // column-level checkboxSelection / headerCheckboxSelection
        // options are deprecated in v32.2 in favour of
        // rowSelection.checkboxes, but that only supports the
        // auto-injected column form — so keep the deprecated path
        // until ag-grid offers an in-column form again.
        checkboxSelection: true,
        headerCheckboxSelection: true,
    },
    {
        headerName: 'Name',
        field: 'name',
        minWidth: 100,
        maxWidth: 1200,
        flex: 1,
        filterParams: { newRowsAction: "keep" }
    },
    {
        headerName: 'Type',
        field: 'filetype',
        width: 120,
        cellRenderer: typeFormatter,
        filterParams: { newRowsAction: "keep" },
        cellStyle: {textAlign: 'center'}
    },
    {
        headerName: 'Size',
        field: 'size',
        valueFormatter: sizeFormatter,
        width: 120,
        filterParams: { newRowsAction: "keep" },
        cellStyle: {textAlign: 'center'}
    },
    {
        headerName: 'Modified on',
        field: 'modified_on',
        width: 150,
        sort: 'desc',
        sortIndex: 0,
        valueFormatter: dateFormatter,
        cellStyle: {textAlign: 'center'}
    },
    {
        headerName: '',
        width: 60,
        sortable: false,
        filter: false,
        cellRenderer: viewButtonCellRenderer,
        cellStyle: {textAlign: 'center', padding: 0}
    }
]});

const onGridReady = async (params) => {
    gridApi.value = params.api;
    // Pin the initial sort so it survives transactions and any prior
    // grid state. Default direction is newest first; folders fall to
    // the bottom because their modified_on is null (S3 doesn't track
    // a timestamp for CommonPrefixes).
    params.api.applyColumnState({
        state: [{ colId: 'modified_on', sort: 'desc', sortIndex: 0 }],
        defaultState: { sort: null },
    });
    await listFiles(selectedRootPath.value);
};

const onRowSelected = (event) => {
    const grid = gridApi.value;

    selectedNodes.value = grid.getSelectedNodes();
    const selectedData = selectedNodes.value.map( node => node.data );

    const contains_types = (() => {
        const types = {dir: false, file: false};
        for (let item of selectedData) {
            if (types.dir && types.file) { break; } // Don't need to go any further
            if (item.filetype == '/dir') { types.dir = true; }
            else { types.file = true; }
        }
        return types;
    })();

    selectedDataSizes.value = selectedData.map(node => node.size);

    const add = (a, b) => a + b;
    if (selectedDataSizes.value.length > 0) {
        status.value = true;
        const totalSize = { value: (contains_types.file)? selectedDataSizes.value.reduce(add) : null };
        selectedDataTotal.value = ((contains_types.dir && contains_types.file)? '> ':'') + sizeFormatter(totalSize);
    } else { status.value = false; }
};


const onRowDoubleClicked = async (event) => {
    const grid = gridApi.value;
    const node = event.node;

    if (node.data.filetype == '/dir') {
        await listFiles(node.data.key);
    } else {
      downloadFile(node.data.key, node.data.filetype);
    }
}

const downloadFile = async (key, filename) => {
  // /api/download returns { url }; the URL itself carries
  // Content-Disposition: attachment so opening it triggers a save.
  const response = await axios.get(import.meta.env.VITE_API_BACKEND_URL+'/api/download', {
    params: { key, filename },
    withCredentials: true,
  });
  const a = document.createElement('a');
  a.href = response.data.url;
  a.click();
};


// In-app document viewer. Reuses the existing /api/download endpoint
// (which returns a 1-hour presigned S3 URL) and renders the file
// inline by mode:
//   pdf   -> <iframe>
//   image -> <img>
//   text  -> fetch the URL, drop the body into <pre>
// Anything else surfaces a "no preview" message with a Download
// fallback. Editable upstream by extending the lookup below.
const PDF_EXTS = new Set(['pdf']);
const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp']);
const HTML_EXTS = new Set(['html', 'htm']);
const TEXT_EXTS = new Set([
  'txt', 'md', 'log', 'json', 'csv', 'tsv', 'yaml', 'yml',
  'xml', 'js', 'ts', 'css', 'py', 'sh', 'ini', 'conf',
]);

const showViewModal = ref(false);
const viewFile = ref(null);

function viewerModeFor(ext) {
  ext = String(ext || '').toLowerCase();
  if (PDF_EXTS.has(ext)) return 'pdf';
  if (IMAGE_EXTS.has(ext)) return 'image';
  if (HTML_EXTS.has(ext)) return 'html';
  if (TEXT_EXTS.has(ext)) return 'text';
  return 'unsupported';
}

async function fetchPreviewText(key) {
  // Proxy text through the API: keeps fetch same-origin (no CORS)
  // and forces text/plain so HTML / JS source render as text rather
  // than executing. The backend caps response size; the
  // X-Preview-Truncated header signals when that kicked in.
  const res = await axios.get(import.meta.env.VITE_API_BACKEND_URL + '/api/files/preview-text', {
    params: { key },
    responseType: 'text',
    withCredentials: true,
    transformResponse: [(d) => d], // keep raw text; axios's default JSON-parses anything that *looks* like JSON
  });
  let text = typeof res.data === 'string' ? res.data : String(res.data ?? '');
  if (res.headers?.['x-preview-truncated'] === '1') {
    text += '\n\n…[truncated by server]';
  }
  return text;
}

async function fetchPresignedUrl(key) {
  const res = await axios.get(import.meta.env.VITE_API_BACKEND_URL + '/api/download', {
    params: { key },
    withCredentials: true,
  });
  return res.data.url;
}

const openViewer = async (node) => {
  const mode = viewerModeFor(node.filetype);
  viewFile.value = {
    name: node.name,
    key: node.key,
    filetype: node.filetype,
    mode,
    url: null,
    text: null,
    // For HTML files we render the live document by default and let
    // the user flip to source. The iframe is sandboxed (no scripts,
    // no same-origin), so executing markup can't reach the API or
    // session — see the security note on the toggle button.
    htmlView: mode === 'html' ? 'rendered' : null,
    error: null,
    loading: mode !== 'unsupported',
  };
  showViewModal.value = true;
  if (mode === 'unsupported') return;

  try {
    if (mode === 'text') {
      const text = await fetchPreviewText(node.key);
      if (!viewFile.value || viewFile.value.key !== node.key) return;
      viewFile.value.text = text;
    } else if (mode === 'html') {
      // HTML mode wants both: a presigned URL for the sandboxed
      // iframe (rendered view) and the source text (for the toggle).
      // Fetch in parallel — they're independent.
      const [url, text] = await Promise.all([
        fetchPresignedUrl(node.key),
        fetchPreviewText(node.key),
      ]);
      if (!viewFile.value || viewFile.value.key !== node.key) return;
      viewFile.value.url = url;
      viewFile.value.text = text;
    } else {
      // PDF / image: presigned URL only. iframe and <img> don't
      // enforce CORS for display, and we want the bucket's
      // Content-Type to drive rendering.
      const url = await fetchPresignedUrl(node.key);
      if (!viewFile.value || viewFile.value.key !== node.key) return;
      viewFile.value.url = url;
    }
  } catch (err) {
    if (viewFile.value && viewFile.value.key === node.key) {
      viewFile.value.error = err.response?.data?.error || err.message || 'Failed to load file';
    }
  } finally {
    if (viewFile.value && viewFile.value.key === node.key) {
      viewFile.value.loading = false;
    }
  }
};

// Drag offset from the flex-centered position. Persisted across
// preview opens so the user's preferred placement sticks while the
// page is loaded. Reset only when the page itself is reloaded.
const modalOffset = ref({ x: 0, y: 0 });
// User-resized card dimensions. null means "use the CSS default" —
// the first time the modal opens we just inherit calc(100vw - 6rem)
// etc.; once a ResizeObserver fires (or the user drags the corner),
// the dimensions stick across opens.
const modalSize = ref({ width: null, height: null });
// True while the user is actively dragging the modal. Used to disable
// pointer events on the body content (iframe / image) so they don't
// swallow the mousemove if the cursor crosses over them mid-drag.
const modalDragging = ref(false);

const viewerCardRef = ref(null);

// Inline-style binding for the card. Combines the drag transform with
// the (optional) persisted width/height. The CSS still defines
// min/max bounds so over- or under-sizing self-corrects.
const viewerCardStyle = computed(() => {
  const style = {
    transform: `translate(${modalOffset.value.x}px, ${modalOffset.value.y}px)`,
  };
  if (modalSize.value.width != null) style.width = modalSize.value.width + 'px';
  if (modalSize.value.height != null) style.height = modalSize.value.height + 'px';
  return style;
});

// Persist size + position across full page reloads via localStorage.
// In-memory persistence (the refs themselves) handles the same-page
// case; localStorage handles refresh, new tab, and re-login.
const VIEWER_LAYOUT_KEY = 's3fm.viewerLayout';

(function loadViewerLayout() {
  try {
    const raw = localStorage.getItem(VIEWER_LAYOUT_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (
      parsed?.offset &&
      Number.isFinite(parsed.offset.x) &&
      Number.isFinite(parsed.offset.y) &&
      // Drop offsets that would land the modal completely off-screen
      // — likely from a previous session on a larger monitor.
      Math.abs(parsed.offset.x) <= window.innerWidth &&
      Math.abs(parsed.offset.y) <= window.innerHeight
    ) {
      modalOffset.value = parsed.offset;
    }
    if (
      parsed?.size &&
      Number.isFinite(parsed.size.width) && parsed.size.width > 0 &&
      Number.isFinite(parsed.size.height) && parsed.size.height > 0
    ) {
      modalSize.value = parsed.size;
    }
  } catch {
    // Corrupted entry, private mode, etc. — fall back to defaults.
  }
})();

watch([modalOffset, modalSize], () => {
  try {
    localStorage.setItem(VIEWER_LAYOUT_KEY, JSON.stringify({
      offset: modalOffset.value,
      size: modalSize.value,
    }));
  } catch {
    // Quota / private mode — nothing to do, in-memory persistence
    // still works for the rest of the session.
  }
}, { deep: true });

// Watch the card via ResizeObserver so we capture user resizes
// (corner handle, programmatic, viewport-driven max clamps). Attach
// when the modal opens; detach on close.
let viewerResizeObserver = null;
watch(showViewModal, async (open) => {
  if (!open) {
    if (viewerResizeObserver) {
      viewerResizeObserver.disconnect();
      viewerResizeObserver = null;
    }
    return;
  }
  await nextTick();
  if (!viewerCardRef.value || typeof ResizeObserver === 'undefined') return;
  // The first observe-fire reports the current (CSS-derived) size —
  // that's fine, we want to capture it so a subsequent close/reopen
  // applies the same size explicitly. After that, real user resizes
  // overwrite it.
  viewerResizeObserver = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (!entry) return;
    modalSize.value = {
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    };
  });
  viewerResizeObserver.observe(viewerCardRef.value);
});

const startDragModal = (e) => {
  // Ignore drags that start on action elements so buttons keep working.
  if (e.target.closest('button, .btn-group')) return;
  e.preventDefault();
  const startX = e.clientX;
  const startY = e.clientY;
  const initX = modalOffset.value.x;
  const initY = modalOffset.value.y;

  modalDragging.value = true;
  document.body.style.userSelect = 'none';

  const onMove = (ev) => {
    modalOffset.value = {
      x: initX + (ev.clientX - startX),
      y: initY + (ev.clientY - startY),
    };
  };
  const onUp = () => {
    modalDragging.value = false;
    document.body.style.userSelect = '';
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  };

  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
};

const closeViewer = () => {
  showViewModal.value = false;
  viewFile.value = null;
  // Intentionally NOT resetting modalOffset / modalSize — they persist
  // across opens so the user's preferred layout sticks. They reset on
  // page reload (the refs are recreated).
};

const downloadFromViewer = () => {
  if (!viewFile.value) return;
  // Direct-link download via the presigned URL we already have.
  downloadFile(viewFile.value.key, viewFile.value.name);
};


const onContextMenu = (params) => {
    const MAX_DOWNLOAD_COUNT = 10;

    const grid = gridApi.value;
    const pointed_node = params.node;

    var on_top_of = selectedNodes.value.find(n => n.id === pointed_node.id && n.data?.key === pointed_node.data.key);
    if (!on_top_of) {
        grid.deselectAll();
        pointed_node.setSelected(true);
        selectedNodes.value = [pointed_node];
    }

    let is_disabled = false;

    showContextMenu({
        x: params.event.x,
        y: params.event.y,
        items: [
          {
            label: "Download",
            disabled: is_disabled,
            onClick: () => { toolbarRef.value.downloadFiles(); }
          },
          {
            label: "Delete",
            onClick: () => { toolbarRef.value.showDeleteFilesModal(); }
          },
        ]
    });
}

onClickOutside(gridElementRef, (event) => {
    const EXCLUDED_ELEMENT_CLASSES = ['file-manager-toolbar', 'mx-menu-ghost-host'];
    const path = getEventPath(event);

    for (let elem of path) {
        if (EXCLUDED_ELEMENT_CLASSES.includes(elem.className)) { return; }
    }

    gridApi.value.deselectAll();
    selectedNodes.value = gridApi.value.getSelectedNodes();
});


const gridOptions = {
    rowHeight: 45,
    columnDefs: columnDefs.value,
    defaultColDef: {
        sortable: true,
        suppressMovable: true
    },
    rowSelection: {
        mode: 'multiRow',
        // Click selection is off so double-clicking a folder navigates
        // into it without also toggling its checkbox. Selection happens
        // via the in-column checkbox above or programmatically (e.g.
        // the right-click handler below).
        enableClickSelection: false,
        // The checkbox column itself is configured per-column above
        // (see filetype column) so it shares space with the file icon
        // instead of getting its own dedicated column.
        checkboxes: false,
        headerCheckbox: false,
    },
    debug: false,
    animateRows: false,
    paginationPageSize: 1000,
    paginationPageSizeSelector: false,
    pagination: true,
    onGridReady: onGridReady,
    //onRowClicked: (params) => {},
    onRowDoubleClicked: onRowDoubleClicked,
    onRowSelected: onRowSelected,
    onCellContextMenu: onContextMenu,
    // ag-grid passes context.* through as params.context.* to every
    // cell renderer. The view-button renderer uses this to call
    // openViewer without needing access to the parent component.
    context: { openViewer }
};


const parsePath = (dirpath) => {
    const rootPath = selectedRootPath.value;
    dirpath = dirpath.slice(rootPath.length);
    const currentPath = thisStore.currentPath;
    let split_dirs = dirpath.split('/').filter(el => el);

    // Get the display name for the root path
    const rootName = getRootPathName(rootPath);
    currentPath.dirs = [{name: rootName, key: rootPath}];

    split_dirs.forEach((name) => {
        const key = currentPath.dirs[currentPath.dirs.length-1].key + name + '/';
        currentPath.dirs.push({name, key});
    })
    currentPath.key = rootPath + dirpath;
}

const getRootPathName = (path) => {

    if (!path) return 'Home';
    const parts = path.split('/').filter(p => p);
    return parts[parts.length - 1] || 'Root';
}


function syncUrlDir(dirpath) {
  const url = new URL(window.location.href);
  if (dirpath) {
    if (url.searchParams.get('dir') === dirpath) return;
    url.searchParams.set('dir', dirpath);
  } else {
    url.searchParams.delete('dir');
  }
  window.history.replaceState(null, '', url.toString());
}

const listFiles = async (dirpath) => {
  // Drop any in-flight quick filter when navigating to a new directory.
  if (quickFilter.value) {
      quickFilter.value = '';
      if (gridApi.value) gridApi.value.setGridOption('quickFilterText', '');
  }
  // Bail (silently) when either prerequisite isn't ready yet.
  // - gridApi is set in onGridReady.
  // - selectedRootPath / dirpath is set in onMounted after the grants
  //   fetch completes.
  // Whichever of those callbacks loses the race calls listFiles before
  // both are met; the *other* one will fire again with everything in
  // place and actually populate the grid. Without this gate, a too-early
  // call against "/" would 403 and leave a stale error toast behind a
  // grid that successfully loaded on the second try.
  if (!gridApi.value || !dirpath) return;

  const grid = gridApi.value;

  try {
    grid.showLoadingOverlay();
    status.value = false;

    parsePath(dirpath);

    let response = await axios.get(`${import.meta.env.VITE_API_BACKEND_URL}/api/files`, {
      params: { dir: dirpath },
      withCredentials: true
    });


    if (response.data.error) {
      alertStore.error("The current directory could not be accessed.");
      dirpath = selectedRootPath.value;
      parsePath(dirpath);

      response = await axios.get(`${import.meta.env.VITE_API_BACKEND_URL}/api/files`, {
        params: { dir: dirpath },
        withCredentials: true
      });

      if (response.data.error) throw response.data.error;
    }

    const directory_data = response.data;

    grid.setGridOption('rowData', []);

    grid.applyTransaction({
      add: directory_data.store.dirs.map((dir) => ({
        name: dir.name ? path.basename(dir.name) : '',
        filetype: '/dir',
        size: null,
        modified_on: null,
        key: dir.key
      }))
    });

    grid.applyTransaction({
      add: directory_data.store.objects.map((obj) => {
        const file = path.parse(obj.key);
        const filetype = file.ext && file.ext.length > 0 ? file.ext.substring(1) : '';

        return {
          name: file.base,
          filetype,
          size: obj.size,
          modified_on: obj.modified_on,
          key: obj.key
        };
      })
    });

    // Reflect the current directory in the URL so a refresh / reload
    // / shared link restores the same view rather than snapping back to
    // the first root. replaceState avoids polluting browser history and
    // doesn't fire vue-router's navigation guards (which would otherwise
    // re-fetch the session and clear alerts on every drill-in).
    syncUrlDir(dirpath);

    grid.hideOverlay();
  } catch (e) {
    console.error(e);
    alertStore.error("Unable to access S3 Storage.");
    gridApi.value?.hideOverlay();
  }
};

const onFileDragEnter = () => { isFileDraggedOver.value = true; }
const onFileDragLeave = (e) => { console.log(e); isFileDraggedOver.value = false; }
const onFileDrop = async (e) => {
    const files = await getFilesFromDataTransferItems(e.dataTransfer.items);
    isFileDraggedOver.value = false;
    toolbarRef.value.uploadFiles(files);
}

const refresh = async () => {
    await listFiles(currentPath.key);
}

const addFolder = ({name, key}) => {
    gridApi.value.applyTransaction({ add: [{name, filetype:'/dir', size:null, modified_on:null, key}]});
}

const onSelectionChanged = (event) => {

  const selectedRows = event.api.getSelectedRows();

  selectedNodes.value = selectedRows;
};

const switchRootPath = async (path) => {
  selectedRootPath.value = path;
  await listFiles(path);
};

const getPathItems = () => {
    const dirs = currentPath?.dirs;
    if (!dirs?.length) return;
    // Show every segment. The breadcrumb row scrolls horizontally
    // if the path is genuinely too long for the viewport.
    return dirs.map(d => ({ obj: d, collapsed: null }));
}

const rightClickHandler = (e) => { e.preventDefault(); }
const preventBodyDefaults = (e) => { e.preventDefault(); } // For file drag-and-drop functionality

const dragDropEvents = ['dragenter', 'dragover', 'dragleave', 'drop'];



onMounted(async () => {
  try {
    const res = await axios.get(import.meta.env.VITE_API_BACKEND_URL+'/api/files/root-paths', { withCredentials: true });
    rootPaths.value = res.data.paths;
    if (rootPaths.value.length) {
      // Honor ?dir=... if it falls inside one of the user's roots —
      // that lets refresh / bookmarks restore the deep position. If
      // it's missing or no longer accessible (e.g. the grant was
      // revoked), fall back to the first root.
      const initialDir = new URLSearchParams(window.location.search).get('dir');
      const matchingRoot = initialDir
        ? rootPaths.value.find(r => initialDir === r || initialDir.startsWith(r))
        : null;

      if (matchingRoot) {
        selectedRootPath.value = matchingRoot;
        listFiles(initialDir);
      } else {
        selectedRootPath.value = rootPaths.value[0];
        listFiles(selectedRootPath.value);
      }
    }
  } catch (err) {
    console.error(err);
  }


    dragDropEvents.forEach((event) => {
        document.body.addEventListener(event, preventBodyDefaults)
    })
});

onUnmounted(() => {
    dragDropEvents.forEach((event) => {
        document.body.removeEventListener(event, preventBodyDefaults)
    })
})

</script>


<template>
    <div class="container-fluid" id="explorer-container">
        <h1>File manager</h1>
        <div class="d-flex flex-column" id="explorer-header">
            <div class="w-100 p-0" id="header-status-bar">
                <div class="card m-0 px-2" id="status-card">
                  <div class="card-body p-0">

                      <div class="d-flex flex-row flex-nowrap p-1" id="current-path-items">

                        <div v-if="rootPaths && rootPaths.length" class="d-flex flex-row flex-shrink-1">
                          <div class="dropdown">
                            <!-- Multi-root: dropdown switcher; single-root: plain button that re-lists. -->
                            <button v-if="rootPaths.length > 1"
                                    class="btn btn-primary dropdown-toggle mx-0 px-3"
                                    type="button" data-bs-toggle="dropdown" aria-expanded="false">
                              <font-awesome-icon icon="fa-solid fa-folder" class="me-1" />
                              {{ getRootPathName(selectedRootPath) }}
                            </button>
                            <button v-else
                                    class="btn btn-primary mx-0 px-3"
                                    type="button"
                                    @click="switchRootPath(selectedRootPath)">
                              <font-awesome-icon icon="fa-solid fa-folder" class="me-1" />
                              {{ getRootPathName(selectedRootPath) }}
                            </button>
                            <ul v-if="rootPaths.length > 1" class="dropdown-menu">
                              <li v-for="path in rootPaths" :key="path">
                                <a class="dropdown-item" :class="{ 'active': path === selectedRootPath }" @click="switchRootPath(path)">
                                  <font-awesome-icon icon="fa-solid fa-folder" class="me-2" />
                                  <span>{{ getRootPathName(path) }}</span>
                                </a>
                              </li>
                            </ul>
                          </div>
                        </div>

                        <div class="d-flex flex-row flex-nowrap path-segments">
                            <div v-for="(item, index) in getPathItems()" :key="index" class="d-flex flex-row flex-shrink-1" v-bind:class="(index==0 || item.collapsed)?'':'collapsible'" >

                                <div v-if="(index>0)" class="m-2">
                                    <font-awesome-icon icon="fa-solid fa-chevron-right" />
                                </div>
                                <div v-if="item.collapsed" class="dropdown">
                                  <button class="btn btn-light dropdown-toggle mx-0 px-3" type="button" id="pathsDropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false" @focus="unfocus">...</button>
                                  <ul class="dropdown-menu" aria-labelledby="pathsDropdownMenuButton">
                                    <li v-for="subitem in item.collapsed">
                                        <a class="dropdown-item" @click="listFiles(subitem.key)"><span>{{subitem.name}}</span></a>
                                    </li>
                                  </ul>
                                </div>
                                <div v-if="item.obj && (index > 0)" class="collapsible">
                                    <button type="button" class="btn btn-light w-100 mx-0 px-2" @click="listFiles(item.obj.key)"><div class="text-truncate">{{item.obj.name}}</div></button>
                                </div>
                            </div>
                        </div>
                    </div>

                  </div>
                </div>

            </div>

            <div class="d-flex flex-row flex-nowrap mt-1 align-items-stretch" id="header-actions-row">
                <div class="flex-grow-1 d-flex align-items-stretch p-1" id="header-search-bar">
                    <div class="input-group filter-input-group">
                        <span class="input-group-text"><font-awesome-icon icon="fa-solid fa-magnifying-glass" /></span>
                        <input type="text" class="form-control" placeholder="Search this folder..."
                               v-model="quickFilter" @input="onQuickFilterInput" aria-label="Search files in current folder">
                        <button type="button" class="btn btn-outline-secondary"
                                v-show="quickFilter" @click="clearQuickFilter" title="Clear filter">
                            <font-awesome-icon icon="fa-solid fa-xmark" />
                        </button>
                    </div>
                </div>

                <div class="ps-1 flex-grow-0" id="header-tool-bar">
                    <fileManagerToolbar ref="toolbarRef" class="file-manager-toolbar"
                                        v-bind:nodes="selectedNodes"
                                        @refresh="refresh"
                                        @selection-changed="onSelectionChanged"
                                        @createFolder="addFolder" />
                </div>
            </div>
        </div>
        <div ref="fileManagerBoxRef" id="file-manager-box" @dragenter="onFileDragEnter" >
            <div id="grid-overlay" class="overlay-sensitive"  v-show="isFileDraggedOver" >
                <div id="overlay-content"><font-awesome-icon icon="fa-solid fa-cloud-arrow-up" size="5x" style="color: #850f5f" /></div>
                <div class="overlay-sensitive" @dragleave="onFileDragLeave" @drop.prevent="onFileDrop" @dragover.prevent @dragenter.prevent></div>
            </div>
            <ag-grid-vue class="ag-theme-material"
                :gridOptions="gridOptions"
                :rowData="rowData.value"
                @contextmenu="rightClickHandler($event)"
                ref="gridElementRef"
            ></ag-grid-vue>
            <!-- In-app document viewer modal -->
            <div v-if="showViewModal" class="viewer-modal" @keyup.esc="closeViewer">
              <div class="viewer-backdrop" @click="closeViewer"></div>
              <div class="viewer-card"
                   ref="viewerCardRef"
                   :style="viewerCardStyle">
                <div class="viewer-header" @mousedown="startDragModal">
                  <strong class="text-truncate me-2">{{ viewFile?.name }}</strong>
                  <div class="d-flex gap-2 flex-shrink-0 align-items-center">
                    <!-- HTML view toggle: rendered (sandboxed iframe) vs source. -->
                    <div v-if="viewFile?.mode === 'html'" class="btn-group btn-group-sm" role="group">
                      <button type="button"
                              class="btn"
                              :class="viewFile.htmlView === 'rendered' ? 'btn-primary' : 'btn-outline-secondary'"
                              @click="viewFile.htmlView = 'rendered'">
                        Rendered
                      </button>
                      <button type="button"
                              class="btn"
                              :class="viewFile.htmlView === 'source' ? 'btn-primary' : 'btn-outline-secondary'"
                              @click="viewFile.htmlView = 'source'">
                        Source
                      </button>
                    </div>
                    <button class="btn btn-sm btn-outline-secondary"
                            :disabled="!viewFile?.url && !viewFile?.text"
                            @click="downloadFromViewer">
                      <font-awesome-icon icon="download" /> Download
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" @click="closeViewer">
                      <font-awesome-icon icon="xmark" /> Close
                    </button>
                  </div>
                </div>
                <div class="viewer-body" :class="{ 'viewer-body--dragging': modalDragging }">
                  <div v-if="viewFile?.error" class="text-center text-danger p-4">{{ viewFile.error }}</div>
                  <div v-else-if="viewFile?.mode === 'unsupported'" class="text-center text-muted p-4">
                    No in-app preview for <code>.{{ viewFile.filetype || 'file' }}</code> files.
                    <div class="mt-2"><button class="btn btn-link p-0" @click="downloadFromViewer">Download instead</button></div>
                  </div>
                  <div v-else-if="viewFile?.loading" class="text-center text-muted p-4">Loading…</div>
                  <iframe v-else-if="viewFile?.mode === 'pdf' && viewFile.url" :src="viewFile.url" class="viewer-iframe"></iframe>
                  <div v-else-if="viewFile?.mode === 'image' && viewFile.url" class="viewer-image-wrap">
                    <img :src="viewFile.url" :alt="viewFile.name" />
                  </div>
                  <!-- Sandboxed iframe with no allow-* values: scripts disabled,
                       forms disabled, treated as a unique opaque origin so the
                       loaded HTML can't access cookies / storage / our API. -->
                  <iframe v-else-if="viewFile?.mode === 'html' && viewFile.htmlView === 'rendered' && viewFile.url"
                          :src="viewFile.url"
                          sandbox=""
                          referrerpolicy="no-referrer"
                          class="viewer-iframe"></iframe>
                  <pre v-else-if="(viewFile?.mode === 'text' || (viewFile?.mode === 'html' && viewFile.htmlView === 'source')) && viewFile.text != null"
                       class="viewer-pre">{{ viewFile.text }}</pre>
                </div>
              </div>
            </div>

            <div class="row mt-2 px-2" id="footer-status-bar">
                <div class="col-4">
                    <div  v-if="status" class="card p-2 bg-light" id="status-card">
                      <div class="card-body p-0">
                          <div class="row">
                            <div class="col-6"><p class="card-text text-center"><strong>{{ selectedDataSizes.length }}</strong> File(s) selected</p></div>
                            <div class="col-6"><p class="card-text text-center">Total size: <strong>{{ selectedDataTotal }}</strong></p></div>
                          </div>
                      </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
.ag-theme-material {
  font-size: 14px;
  // Fill the viewport down from the grid's position. The fixed
  // offset budgets for the page chrome above (nav + h1 + breadcrumbs
  // + actions row) and a small margin below; min-height keeps
  // ~6 rows visible on short windows.
  height: calc(100vh - 300px);
  min-height: 360px;
  width: 100%;
  margin-top: 5px;
}

#file-manager-box {
  position: relative;
}

#header-search-bar {
  .filter-input-group {
    width: 100%;
  }
}

// Only the path-segments strip scrolls horizontally; the SHARED root
// dropdown stays outside so its menu is free to escape the row.
#current-path-items .path-segments {
  overflow-x: auto;
  flex: 1 1 auto;
  min-width: 0;
}

.overlay-sensitive {
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  text-align: center;
}

#grid-overlay {
  background-color: rgba(200,195,200,0.5);
}

#grid-overlay #overlay-content {
  display: block;
  position: relative;
  top: 50%;
  transform: translateY(-60%);
}

.viewer-modal {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
}
.viewer-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
}
.viewer-card {
  position: relative;
  /* Default: large but with a comfortable gutter so the user can see
     they're in a modal. `resize: both` gives a drag handle at the
     bottom-right corner; mousedown on the header drags the whole
     card. min/max keep things usable. */
  width: calc(100vw - 6rem);
  height: calc(100vh - 6rem);
  min-width: 320px;
  min-height: 240px;
  max-width: calc(100vw - 1rem);
  max-height: calc(100vh - 1rem);
  resize: both;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e5e5;
  flex-shrink: 0;
  cursor: move;
  user-select: none;
}
.viewer-body {
  flex: 1;
  min-height: 0;
  display: flex;
  background: #f8f8f8;
}
/* While dragging the modal, anything inside the body must not
   intercept pointer events — otherwise the iframe (PDF / sandboxed
   HTML) or image swallows mousemove and the drag stalls when the
   cursor crosses over it. */
.viewer-body--dragging * {
  pointer-events: none;
}
.viewer-iframe {
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}
.viewer-image-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 1rem;
}
.viewer-image-wrap img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.viewer-pre {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 1rem;
  overflow: auto;
  background: #fff;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
}
</style>